import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { SelectPersonTypeSchema } from "../../../../../validators/selectPersonTypeSchema";
import { useEffect, useState } from "react";

import { Button } from "../../../../../components/Button";
import { Radio } from "../../../../../components/Radio";
import { Checkbox } from "../../../../../components/CheckboxMeetings";
import { useAuth } from "../../../../../contexts/AuthContext";
import { CountrySelector } from "../../../../../components/CountrySelector";
import { PersonType } from "../../../../../enumns/personType";
import { PaymentTypeEnum } from "../../../../../enumns/paymentType";
import { useUpdateExpertDataMutation } from "../../../../../services/hooks/experts/mutations/useUpdateExpertDataMutation";
import { useExpertQuery } from "../../../../../services/hooks/experts/queries/useExpertQuery";
import { Tooltip } from "../../../../../components/Tooltip";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { TOAST_ERROR_DEFAULT_OPTIONS } from "../../../../../constants/toast";
import Select, { SelectItem } from "../../../../../components/Select";
import { HowDiscoverUsTypeEnum } from "../../../../../enumns/howDiscoverUsType";

import register1 from "../../../../../assets/images/register-1.png";
import Markdown from "react-markdown";

type SelectPersonTypeProps = {
  onGoToNextStep: () => void;
};

export const SelectPersonType = ({ onGoToNextStep }: SelectPersonTypeProps) => {
  const { user } = useAuth();
  const { data } = useExpertQuery(user?.id!);
  const { formatMessage } = useIntl();
  const { mutate, isLoading } = useUpdateExpertDataMutation();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{
    personType: `${PersonType}`;
    nationality: string;
    bankCountry: string;
    meetingLanguages: string[];
    otherLanguage?: string;
    howDiscoverUs: string;
  }>({
    mode: "onChange",
    resolver: yupResolver(SelectPersonTypeSchema()),
    defaultValues: (() => {
      const value = JSON.parse(
        sessionStorage.getItem("formSelectPersonType") || "null"
      );

      if (value === null) {
        return {
          personType: data?.personType,
          nationality: "",
          bankCountry: "",
          meetingLanguages: [],
          otherLanguage: "",
          howDiscoverUs: "",
        };
      }

      return {
        personType: data?.personType,
        nationality: value?.nationality ?? "",
        bankCountry: value?.bankCountry ?? "",
        meetingLanguages: [...(value?.meetingLanguages ?? [])],
        otherLanguage: value?.otherLanguage ?? "",
        howDiscoverUs: value?.howDiscoverUs ?? "",
      };
    })(),
  });

  const nationality = watch("nationality");
  const bankCountry = watch("bankCountry");
  const meetingLanguages = watch("meetingLanguages");

  const [personTypeOptions, setPersonTypeOptions] = useState<
    Array<{ type: PersonType | PaymentTypeEnum; label: string }>
  >([]);

  useEffect(() => {
    setPersonTypeOptions(getPersonTypeOptions());
  }, [nationality, bankCountry]);

  // const paymentTypeOptions = [
  //   {
  //     label: 'PAYMENT_OUTSIDE_BRAZIL',
  //     type: PaymentTypeEnum.PAYMENT_OUTSIDE_BRAZIL,
  //   },
  //   {
  //     label: 'PAYMENT_WITHIN_BRAZIL',
  //     type: PaymentTypeEnum.PAYMENT_WITHIN_BRAZIL,
  //   },
  //   {
  //     label: 'NO_PAYMENT',
  //     type: PaymentTypeEnum.NO_PAYMENT,
  //   },
  // ]

  const getNationalityAndBankCountryOption = (isBankCountry = false) => {
    const option = isBankCountry ? bankCountry : nationality;

    if (!option && !option.length) {
      return undefined;
    }

    switch (option) {
      // alpha_3_code que pode ser encontrado no mock countryAndNationalityList.ts
      case "BRA":
        return "brazil";

      default:
        return "international";
    }
  };

  const getPersonTypeOptions = () => {
    const optionsMap: Record<string, Array<PersonType | PaymentTypeEnum>> = {
      brazil_brazil: [
        PersonType.NATURAL_PERSON,
        PersonType.LEGAL_PERSON,
        PaymentTypeEnum.NO_PAYMENT,
      ],
      brazil_international: [
        PersonType.INTERNATIONAL_PERSON,
        PaymentTypeEnum.NO_PAYMENT,
      ],
      international_brazil: [
        PersonType.NATURAL_PERSON,
        PersonType.LEGAL_PERSON,
        PaymentTypeEnum.NO_PAYMENT,
      ],
      international_international: [
        PersonType.INTERNATIONAL_PERSON,
        PaymentTypeEnum.NO_PAYMENT,
      ],
    };

    const key = `${getNationalityAndBankCountryOption()}_${getNationalityAndBankCountryOption(
      true
    )}`;

    return (optionsMap[key] || []).map((type) => ({
      type,
      label: type.toUpperCase(),
    }));
  };

  const onSubmitPersonType = (value: {
    personType: `${PersonType}` | `${PaymentTypeEnum}`;
    // paymentType: PaymentTypeEnum
    nationality: string;
    bankCountry: string;
    meetingLanguages: string[];
    otherLanguage?: string;
  }) => {
    if (data?.id) {
      const nationalityData =
        value.nationality === "BRA" ? "brazil" : "international";
      const bankCountryData =
        value.bankCountry === "BRA" ? "brazil" : "international";
      const processedMeetingLanguages = value.meetingLanguages.filter(
        (lang) => lang !== "other"
      );

      if (value.otherLanguage && value.otherLanguage.trim() !== "") {
        processedMeetingLanguages.push(value.otherLanguage);
      }

      const paymentTypeData = (() => {
        if (value.personType === PaymentTypeEnum.NO_PAYMENT) {
          return PaymentTypeEnum.NO_PAYMENT;
        }

        if (bankCountryData === "brazil") {
          return PaymentTypeEnum.PAYMENT_WITHIN_BRAZIL;
        }
        return PaymentTypeEnum.PAYMENT_OUTSIDE_BRAZIL;
      })();

      const personTypeData = (() => {
        if (value.personType === PaymentTypeEnum.NO_PAYMENT) {
          return PersonType.NATURAL_PERSON;
        }

        if (paymentTypeData === PaymentTypeEnum.PAYMENT_OUTSIDE_BRAZIL) {
          return PersonType.INTERNATIONAL_PERSON;
        }

        if (value.personType === PersonType.LEGAL_PERSON) {
          return PersonType.LEGAL_PERSON;
        }
        if (value.personType === PersonType.NATURAL_PERSON) {
          return PersonType.NATURAL_PERSON;
        }
        return PersonType.INTERNATIONAL_PERSON;
      })();

      sessionStorage.setItem("formSelectPersonType", JSON.stringify(value));

      mutate(
        {
          id: data.id,
          expertData: {
            personType: personTypeData,
            paymentType: paymentTypeData,
            nationality: nationalityData,
            bankCountry: bankCountryData,
            meetingLanguages: processedMeetingLanguages,
            otherLanguage: value.otherLanguage,
          },
        },
        {
          onSuccess() {
            onGoToNextStep();
          },
          onError(error) {
            toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS);
          },
        }
      );
    }
  };

  return (
    <section className="md:py-5">
      <div className="container mx-auto">
        <div className="flex justify-center">
          <div className="hidden md:block w-1/4">
            <img src={register1} alt="Text with image" />
          </div>
          <div className="w-full md:w-3/4 md:ps-8">
            <form
              onSubmit={handleSubmit(onSubmitPersonType)}
              className="flex flex-col w-full h-full lg:max-w-[90%]"
            >
              <header className="flex flex-wrap mb-12">
                <h2 className="mb-6 text-2xl font-semibold">
                  {formatMessage({ id: "PAYMENT_TYPE_START_REGISTER_TITLE" })}
                </h2>
                <h2 className="text-sm">
                  {formatMessage({
                    id: "PAYMENT_TYPE_START_REGISTER_SUBTITLE",
                  })}
                </h2>
              </header>

              <div id="inputs-button" className="w-full">
                <div id="grid" className="grid grid-cols-1">
                  <div
                    className="grid grid-cols-2 gap-6 mb-8"
                    id="nacionality-bank"
                  >
                    <div
                      id="nacionality-field"
                      className="flex flex-col justify-end"
                    >
                      <label className="flex items-center gap-2 mb-4 text-sm font-medium">
                        {formatMessage({ id: "REQUIRED_NATIONALITY" })}
                      </label>
                      <Controller
                        name="nationality"
                        control={control}
                        render={({ field }) => (
                          <CountrySelector
                            value={field.value}
                            onValueChange={field.onChange}
                            isNationalityList={true}
                            placeholderMessageId={"REQUIRED_NATIONALITY"}
                          />
                        )}
                      />
                    </div>

                    <div id="bank-country-field">
                      <label className="flex items-center gap-2 mb-4 text-sm font-medium">
                        {formatMessage({ id: "REQUIRED_BANK_COUNTRY" })}
                      </label>
                      <Controller
                        name="bankCountry"
                        control={control}
                        render={({ field }) => (
                          <CountrySelector
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholderMessageId={"SELECT_COUNTRY_BANK_ACCOUNT"}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {bankCountry && (
                    <div id="person-type">
                      <div id="person-type-field">
                        <label className="flex flex-col mb-4 text-sm">
                          {bankCountry === "BRA" ? (
                            <>
                              <span className="mb-4">
                                {formatMessage({ id: "PERSON_TYPE_BRAZIL" })}
                              </span>
                            </>
                          ) : (
                            <Markdown
                              components={{
                                p: ({ children, ...rest }) => (
                                  <p className="border-s-[12px] border-secondary-light ps-4 mb-10">
                                    {children}
                                  </p>
                                ),
                              }}
                            >
                              {formatMessage({
                                id: "PERSON_TYPE_INTERNATIONAL",
                              })}
                            </Markdown>
                          )}
                        </label>
                        {bankCountry === "BRA" && (
                          <div className="mb-16">
                            <Controller
                              name="personType"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <Radio.Root
                                  name="personType"
                                  onValueChange={onChange}
                                  value={value}
                                >
                                  <div className="flex flex-col gap-6">
                                    {personTypeOptions.map(
                                      ({ type, label }) => (
                                        <div
                                          key={type}
                                          className="flex items-center gap-2"
                                        >
                                          <Radio.Item id={type} value={type}>
                                            <Radio.Label htmlFor={type}>
                                              {formatMessage({ id: label })}
                                            </Radio.Label>
                                            <div className="self-start">
                                              <Tooltip>
                                                {formatMessage({
                                                  id: `PAYMENT_METHOD_${label}_INFO`,
                                                })}
                                              </Tooltip>
                                            </div>
                                          </Radio.Item>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </Radio.Root>
                              )}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* <div id="payment-method">
            <div id="payment-type-field">
              <label className="flex items-center gap-2 mb-4 text-sm font-medium">
                {formatMessage({ id: 'PAYMENT_METHOD_TYPE' })}
                <Tooltip>
                  {formatMessage({ id: 'REQUIRED_PAYMENT_METHOD' })}
                </Tooltip>
              </label>
              <div className="mb-16">
                <Controller
                  name="paymentType"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Radio.Root
                      name="paymentType"
                      onValueChange={onChange}
                      value={value}
                    >
                      <div className="flex flex-col gap-8">
                        {paymentTypeOptions.map(({ type, label }) => (
                          <div key={type} className="flex items-center gap-2">
                            <Radio.Item id={type} value={type}>
                              <Radio.Label htmlFor={type}>
                                {formatMessage({ id: label })}
                              </Radio.Label>
                              {type === 'no_payment' ? (
                                <Tooltip>
                                  {formatMessage({
                                    id: `PAYMENT_METHOD_${label}_INFO`,
                                  })}
                                </Tooltip>
                              ) : undefined}
                            </Radio.Item>
                          </div>
                        ))}
                      </div>
                    </Radio.Root>
                  )}
                />
              </div>
            </div>
          </div> */}
                </div>

                <div
                  id="meeting-language-field"
                  className="pb-6 border border-gray-400 rounded-[36px] rounded-tl-none p-5"
                >
                  <label className="flex items-center gap-2 ps-5 pe-8 -mt-7 mb-5 -ms-5 w-fit text-sm font-medium bg-white">
                    {formatMessage({ id: "MEETING_LANGUAGES" })}
                    <Tooltip>
                      {formatMessage({ id: "REQUIRED_MEETING_LANGUAGES" })}
                    </Tooltip>
                  </label>
                  <Controller
                    name="meetingLanguages"
                    control={control}
                    render={({ field }) => (
                      <div className="text-sm">
                        <Checkbox
                          className="grid grid-cols-[140px_130px] gap-4"
                          options={[
                            {
                              label: formatMessage({ id: "PORTUGUESE" }),
                              value: "PT",
                            },
                            {
                              label: formatMessage({ id: "ENGLISH" }),
                              value: "EN",
                            },
                            {
                              label: formatMessage({ id: "SPANISH" }),
                              value: "ES",
                            },
                            {
                              label: formatMessage({ id: "OTHER_LANGUAGES" }),
                              value: "other",
                            },
                          ]}
                          onChange={field.onChange}
                          selectedValues={field.value}
                        />
                        {meetingLanguages.includes("other") && (
                          <div className="mt-4">
                            <Controller
                              name="otherLanguage"
                              control={control}
                              render={({ field }) => (
                                <>
                                  <input
                                    type="text"
                                    className={`w-full  md:w-96 h-10 p-2 border rounded ${
                                      errors.otherLanguage
                                        ? "border-red-500"
                                        : "border-gray-300"
                                    }`}
                                    placeholder={formatMessage({
                                      id: "ADD_LANGUAGES",
                                    })}
                                    {...field}
                                  />
                                  {errors.otherLanguage && (
                                    <p className="text-red-500 text-xs mt-1">
                                      {errors.otherLanguage.message}
                                    </p>
                                  )}
                                </>
                              )}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div id="hear-about-us-field" className="mt-6">
                  <label className="flex flex-col gap-1 ps-4 mb-4 text-sm border-s-[12px] border-secondary-light">
                    <span className="font-semibold">
                      {formatMessage({ id: "HOW_DISCOVER_TRUST" })}
                    </span>
                    <span>
                      {formatMessage({ id: "WE_WANT_TO_KNOW_HOW_YOU_FOUND" })}
                    </span>
                  </label>
                  <Controller
                    name="howDiscoverUs"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          {Object.values(HowDiscoverUsTypeEnum).map((value) => (
                            <SelectItem key={value} value={value}>
                              {formatMessage({ id: value })}
                            </SelectItem>
                          ))}
                        </Select>
                      </>
                    )}
                  />
                </div>

                <div className="flex flex-col md:flex-row justify-center items-center gap-4 w-full mt-10 md:mt-16">
                  <Button
                    type="button"
                    onClick={() => navigate("/expert")}
                    variant="outline"
                    borderColor="border-primary-light"
                    size="large"
                  >
                    {formatMessage({ id: "FINISH_LATER" })}
                  </Button>

                  <Button
                    type="submit"
                    isLoading={isLoading}
                    size="large"
                    className="pl-12 pe-12"
                  >
                    {formatMessage({ id: "CONTINUE" })}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
